import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError, api } from "@/api/client";
import { useWS, useWSEvent } from "@/messaging/WSContext";
import type {
  PlatformStatus,
  PublishBatchRegisterIn,
  PublishBatchView,
  PublishJobRegisterIn,
  PublishJobView,
} from "./types";

/** Shape returned by ``GET /api/market/bulk-jobs`` — agency-wide list
 *  of recent JobRun rows. The poller registers each entry into the
 *  watcher set so teammates' jobs show up alongside the viewer's own. */
interface AgencyBulkJob {
  job_run_id: string;
  job_id: string | null;
  status: "pending" | "running" | "done" | "error" | "cancelled";
  source_url: string;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  agent_user_id: string | null;
  agent_name: string | null;
  agent_avatar_url: string | null;
  triggered_by_name: string | null;
  triggered_by_avatar_url: string | null;
  is_mine: boolean;
  /** Whether the pipeline targeted SS.ge in addition to MyHome. The
   *  panel reserves an SS portal chip based on this; mis-rendering as
   *  MH-only when the run is publishing to both is the bug this field
   *  fixes. Inferred server-side from the in-memory job hint (for
   *  running rows) or the persisted result payload (for terminal). */
  use_ss: boolean;
  /** Shared by every row spawned from the same bulk-publish call. The
   *  poller groups rows by this instead of registering each one
   *  individually — a several-hundred-listing bulk used to mean a
   *  several-hundred-watcher panel for every teammate who had the page
   *  open, which is what made the browser choke. ``null`` for
   *  single-listing publishes / per-platform retries, which stay on the
   *  per-job path below. */
  batch_id: string | null;
}

/** Aggregate summary shape returned by
 *  ``GET /api/job-runs/batch/{batchId}``. */
interface BatchSummary {
  batch_id: string;
  total: number;
  pending: number;
  running: number;
  done: number;
  error: number;
  cancelled: number;
  paused: number;
  stop_requested: number;
}

const STORAGE_KEY = "agencygrid.publish_progress.jobs.v1";
// Persisted jobs older than this are dropped on rehydrate. Stops a
// long-abandoned tab from clogging the panel with year-old jobs after
// a deploy / browser restore.
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
// Key + TTL for the set of job ids the user has explicitly dismissed or
// cleared. The agency poller below would otherwise re-surface a
// still-recent terminal job, so we tombstone dismissals here. The TTL
// comfortably outlasts the poller's 30-min terminal window, so a
// dismissed row can't reappear before it ages out of /bulk-jobs anyway.
const DISMISSED_KEY = "agencygrid.publish_progress.dismissed.v1";
const DISMISSED_TTL_MS = 2 * 60 * 60 * 1000;
// JobRun-row poll cadence when the WS push isn't delivering. Cheap REST
// GETs; they self-stop on terminal status.
const POLL_INTERVAL_MS = 4_000;
// Hard ceiling on polling for a single job — prevents a stuck job
// from hammering the API forever after a tab is left open.
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
// How long a fresh watcher waits for its first live WS event before
// assuming nobody's pushing (socket down, or the backend's in-memory job
// is gone — e.g. after a deploy) and falling back to REST polling.
const GRACE_MS = 8_000;

// Batches poll the aggregate summary endpoint on a plain interval — no
// WS involvement, no per-job grace/fallback dance. One cheap GET per
// *batch* regardless of whether it holds 5 listings or 5,000, so this
// never scales with bulk size the way per-job polling did.
const BATCH_POLL_INTERVAL_MS = 4_000;
// Bulk runs can genuinely take a long time (hundreds of listings behind
// the pipeline concurrency cap) — give batches a much longer ceiling
// than a single job gets before the panel gives up on it.
const BATCH_POLL_TIMEOUT_MS = 60 * 60 * 1000;
// Key + TTL for batches the user dismissed — mirrors ``DISMISSED_KEY``
// below so the agency-wide poller can't resurrect one a director already
// cleared from their panel.
const DISMISSED_BATCHES_KEY = "agencygrid.publish_progress.dismissed_batches.v1";

/** Per-platform pipeline step sequence. As SSE events stream in we
 *  advance the per-job ``mhStepIdx`` / ``ssStepIdx`` cursor monotonically:
 *  on each event we look for the next matching ``[step, status]`` tuple
 *  AFTER the current cursor, so a repeated step name (the SS pipeline
 *  authenticates twice and emits ``ss_create`` for both draft and publish)
 *  correctly advances rather than snapping back to the first occurrence.
 *  Index → percent maps linearly to (0, 100]; the terminal ``finished``
 *  event flips the row to 100% regardless. */
const MH_STEP_SEQUENCE: Array<[string, string]> = [
  ["queue", "progress"],
  ["scrape", "start"],
  ["scrape", "done"],
  ["auth", "start"],
  ["auth", "done"],
  ["upload", "start"],
  ["upload", "done"],
  ["create", "start"],
  ["create", "done"],
  ["pay", "start"],
  ["pay", "done"],
];

const SS_STEP_SEQUENCE: Array<[string, string]> = [
  ["queue", "progress"],
  ["ss_auth", "start"],
  ["ss_auth", "done"],
  ["ss_create", "start"],
  ["ss_create", "done"],
  ["ss_upload", "start"],
  ["ss_upload", "done"],
  ["ss_auth", "start"],
  ["ss_auth", "done"],
  ["ss_create", "start"],
  ["ss_create", "done"],
];

/** Find the next index in ``seq`` strictly after ``cursor`` matching
 *  ``[step, status]``. Returns ``cursor`` if no further match (idempotent
 *  for repeated/progress events). */
function advanceStepCursor(
  seq: Array<[string, string]>,
  cursor: number,
  step: string,
  status: string,
): number {
  for (let i = cursor + 1; i < seq.length; i++) {
    if (seq[i][0] === step && seq[i][1] === status) return i;
  }
  return cursor;
}

/** Image-upload sub-progress: parse ``"Uploaded N/M images"`` /
 *  ``"SS.ge image N/M"`` from progress messages so the longest step
 *  in the pipeline doesn't show a stalled bar mid-upload. Returns the
 *  fractional progress 0-1, or ``null`` when the message doesn't carry
 *  a count. */
function parseUploadFraction(message: string): number | null {
  if (!message) return null;
  // Matches "5/16" anywhere in the message — the pipeline emits both
  // "Uploaded 5/16" (MH) and "SS.ge image 5/16" (SS) shapes.
  const m = message.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  const i = Number(m[1]);
  const n = Number(m[2]);
  if (!Number.isFinite(i) || !Number.isFinite(n) || n <= 0) return null;
  return Math.max(0, Math.min(1, i / n));
}

/** Convert a step-sequence index into a 0-100 percent. Index ``-1``
 *  (no events seen yet) reports null so the bar stays hidden. */
function percentFromIdx(idx: number, seqLen: number): number | null {
  if (idx < 0) return null;
  return Math.round(((idx + 1) / seqLen) * 100);
}

/** Read per-platform success/failure from a pipeline ``result``
 *  payload. Mirrors ``app.routes.pipeline``'s shape:
 *  - MyHome:   ``result.new_listing_id`` / ``result.result.data.id``
 *              → success; ``result.result.result === false`` → fail.
 *  - SS.ge:    ``result.ss_error`` → fail;
 *              ``result.ss_result.application_id`` → success. */
function resolvePlatformsFromResult(
  result: unknown,
  useSs: boolean,
): {
  mhStatus?: PlatformStatus;
  ssStatus?: PlatformStatus;
  mhUrl?: string | null;
  ssUrl?: string | null;
  mhError?: string | null;
  ssError?: string | null;
} {
  const out: {
    mhStatus?: PlatformStatus;
    ssStatus?: PlatformStatus;
    mhUrl?: string | null;
    ssUrl?: string | null;
    mhError?: string | null;
    ssError?: string | null;
  } = {};
  if (!result || typeof result !== "object") {
    if (!useSs) out.ssStatus = "skipped";
    return out;
  }
  const r = result as Record<string, any>;
  // MyHome
  const mhId = r.new_listing_id ?? r.result?.data?.id ?? null;
  if (mhId) {
    out.mhStatus = "done";
    out.mhUrl = `https://www.myhome.ge/ka/pr/${mhId}/`;
  } else if (r.mh_error) {
    // Partial publish: the MyHome flow raised but the pipeline still
    // returned so the successful SS half wouldn't be lost. ``result`` is
    // null in that shape, so without this branch MyHome renders as neither
    // done nor failed and the row offers no retry.
    out.mhStatus = "error";
    out.mhError = String(r.mh_error);
  } else if (r.result?.result === false) {
    out.mhStatus = "error";
    out.mhError =
      typeof r.result?.message === "string" && r.result.message
        ? r.result.message
        : "MyHome listing creation failed.";
  }
  // SS
  if (!useSs) {
    out.ssStatus = "skipped";
  } else if (r.ss_error) {
    out.ssStatus = "error";
    out.ssError = String(r.ss_error);
  } else {
    const ssApp =
      r.ss_result?.application_id ??
      r.ss_result?.api_response?.application?.realEstateApplicationId ??
      null;
    if (ssApp) {
      out.ssStatus = "done";
      out.ssUrl = `https://home.ss.ge/ka/udzravi-qoneba/${ssApp}`;
    }
  }
  return out;
}

interface PublishProgressContextValue {
  jobs: Record<string, PublishJobView>;
  /** Register a job and start watching it. Pages call this immediately
   *  after the publish endpoint returns the ``job_id`` / ``job_run_id``
   *  pair so the panel comes alive within a tick of submission. */
  register: (job: PublishJobRegisterIn) => void;
  /** Drop a single job from the panel. The user clicks ↻ Retry on a
   *  failed row and the page registers a fresh job in its place. */
  dismiss: (jobId: string) => void;
  /** Clear every terminal (done / error) job. Used by the panel's
   *  "clear" affordance once a batch is fully drained. */
  clearFinished: () => void;
  /** Aggregate bulk-publish batches, keyed by ``batchId``. */
  batches: Record<string, PublishBatchView>;
  /** Register a whole bulk-publish batch and start polling its
   *  aggregate summary. Pages call this once (not per job) right after
   *  a bulk-publish endpoint returns. */
  registerBatch: (b: PublishBatchRegisterIn) => void;
  pauseBatch: (batchId: string) => void;
  resumeBatch: (batchId: string) => void;
  stopBatch: (batchId: string) => void;
  dismissBatch: (batchId: string) => void;
}

const Ctx = createContext<PublishProgressContextValue | null>(null);

/** Internal: per-job watcher state (WS-push bookkeeping + poll-fallback
 *  handles). Held in a ref so resubmits / dismissals can tear it down
 *  cleanly. Progress itself arrives via the single shared WebSocket
 *  (see the ``publish_job.event`` subscription below) rather than a
 *  per-job connection — ``graceTimer``/``gotWsEvent`` just decide when
 *  to fall back to REST polling for a job the socket isn't covering. */
interface Watcher {
  jobId: string;
  jobRunId: string;
  useSs: boolean;
  graceTimer: number | null;
  gotWsEvent: boolean;
  pollTimer: number | null;
  pollUntil: number;
  stopped: boolean;
}

/** One bulk-publish batch's poll handle. Plain interval polling — no WS
 *  subscription, no per-job bookkeeping. See ``BATCH_POLL_INTERVAL_MS``. */
interface BatchWatcher {
  batchId: string;
  pollTimer: number | null;
  pollUntil: number;
  stopped: boolean;
}

export function PublishProgressProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<Record<string, PublishJobView>>(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as {
        savedAt: number;
        jobs: Record<string, PublishJobView>;
      };
      if (
        !parsed.savedAt ||
        Date.now() - parsed.savedAt > STORAGE_TTL_MS
      ) {
        window.localStorage.removeItem(STORAGE_KEY);
        return {};
      }
      // Drop everything older than the TTL window. A done/error job from
      // 2 hours ago shouldn't reappear after a refresh — only in-flight
      // ones should survive across reloads. Backfill the percent /
      // step-cursor fields for jobs persisted before the per-row bar
      // was added so the panel can render them without an undefined
      // check on every read.
      const fresh: Record<string, PublishJobView> = {};
      for (const [id, j] of Object.entries(parsed.jobs ?? {})) {
        if (j.state === "running" || Date.now() - j.startedAt < 30 * 60 * 1000) {
          fresh[id] = {
            ...j,
            // Backfill fields added after this job was persisted so an
            // older localStorage entry still satisfies the current shape.
            mhError: j.mhError ?? null,
            ssError: j.ssError ?? null,
            agentAvatarUrl: j.agentAvatarUrl ?? null,
            triggeredByName: j.triggeredByName ?? null,
            triggeredByAvatarUrl: j.triggeredByAvatarUrl ?? null,
            mhPercent: j.mhPercent ?? (j.state !== "running" ? 100 : null),
            ssPercent:
              j.ssPercent ??
              (j.state !== "running" || j.ssStatus === "skipped" ? 100 : null),
            mhStepIdx: j.mhStepIdx ?? -1,
            ssStepIdx: j.ssStepIdx ?? -1,
          };
        }
      }
      return fresh;
    } catch {
      return {};
    }
  });

  const watchersRef = useRef<Map<string, Watcher>>(new Map());
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  // Bulk-publish batches. Deliberately NOT persisted to localStorage
  // (unlike ``jobs`` above) — a page refresh mid-bulk just lets the
  // agency-wide poller below re-discover the batch within one 5s tick
  // (it already re-scans every JobRun in the agency), which is simple
  // enough that a second persistence layer isn't worth the complexity.
  const [batches, setBatches] = useState<Record<string, PublishBatchView>>({});
  const batchesRef = useRef(batches);
  batchesRef.current = batches;
  const batchWatchersRef = useRef<Map<string, BatchWatcher>>(new Map());

  // Foreign (agency-wide) jobs are registered by the poller, not by the
  // user spawning a publish. We track them here so the localStorage
  // serializer can skip them — persisting foreign jobs would leak
  // teammates' work into another user's localStorage on the same
  // browser, and would also cause a stale phantom row on refresh when
  // the foreign job has since finished. The poller re-discovers
  // currently-running foreign jobs on every mount.
  const foreignJobIdsRef = useRef<Set<string>>(new Set());

  // Job ids the user explicitly dismissed or cleared. Without this the
  // agency-wide poller below re-``register``s a still-recent terminal
  // job on its next tick — the dismissed row pops straight back, briefly
  // flashing "starting…" (``register`` seeds ``state: "running"``).
  // That is the "I can never empty the bucket" bug. Mirrored to
  // localStorage (jobId → dismissed-at ms) so a refresh can't resurrect
  // them either; entries age out after ``DISMISSED_TTL_MS``.
  const dismissedRef = useRef<Map<string, number>>(new Map());
  const dismissedHydratedRef = useRef(false);
  if (!dismissedHydratedRef.current) {
    dismissedHydratedRef.current = true;
    try {
      const raw = window.localStorage.getItem(DISMISSED_KEY);
      if (raw) {
        const cutoff = Date.now() - DISMISSED_TTL_MS;
        for (const [id, ts] of Object.entries(
          JSON.parse(raw) as Record<string, number>,
        )) {
          if (typeof ts === "number" && ts > cutoff) {
            dismissedRef.current.set(id, ts);
          }
        }
      }
    } catch {
      /* corrupt / unavailable storage — start with an empty set */
    }
  }

  /** Tombstone the current dismissed set to localStorage, pruning any
   *  entries past the TTL on the way out. */
  const persistDismissed = useCallback(() => {
    const cutoff = Date.now() - DISMISSED_TTL_MS;
    const obj: Record<string, number> = {};
    for (const [id, ts] of dismissedRef.current) {
      if (ts > cutoff) obj[id] = ts;
      else dismissedRef.current.delete(id);
    }
    try {
      window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(obj));
    } catch {
      /* quota / private mode */
    }
  }, []);

  // Same tombstone pattern as ``dismissedRef``, keyed by ``batchId``
  // instead of ``jobRunId`` — a director dismissing a finished bulk
  // batch shouldn't see it pop back on the agency poller's next tick.
  const dismissedBatchesRef = useRef<Map<string, number>>(new Map());
  const dismissedBatchesHydratedRef = useRef(false);
  if (!dismissedBatchesHydratedRef.current) {
    dismissedBatchesHydratedRef.current = true;
    try {
      const raw = window.localStorage.getItem(DISMISSED_BATCHES_KEY);
      if (raw) {
        const cutoff = Date.now() - DISMISSED_TTL_MS;
        for (const [id, ts] of Object.entries(
          JSON.parse(raw) as Record<string, number>,
        )) {
          if (typeof ts === "number" && ts > cutoff) {
            dismissedBatchesRef.current.set(id, ts);
          }
        }
      }
    } catch {
      /* corrupt / unavailable storage — start with an empty set */
    }
  }

  const persistDismissedBatches = useCallback(() => {
    const cutoff = Date.now() - DISMISSED_TTL_MS;
    const obj: Record<string, number> = {};
    for (const [id, ts] of dismissedBatchesRef.current) {
      if (ts > cutoff) obj[id] = ts;
      else dismissedBatchesRef.current.delete(id);
    }
    try {
      window.localStorage.setItem(DISMISSED_BATCHES_KEY, JSON.stringify(obj));
    } catch {
      /* quota / private mode */
    }
  }, []);

  // Persist on every change — small map, single round-trip to
  // localStorage, well below any serialization concern. Foreign jobs
  // are filtered out so the localStorage stays user-owned.
  useEffect(() => {
    try {
      const persistable: Record<string, PublishJobView> = {};
      for (const [id, j] of Object.entries(jobs)) {
        if (!foreignJobIdsRef.current.has(id)) persistable[id] = j;
      }
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ savedAt: Date.now(), jobs: persistable }),
      );
    } catch {
      /* quota / private mode */
    }
  }, [jobs]);

  /** Mutate one job's record. Spread-and-replace keeps the React state
   *  immutable so ``useState`` selectors see the change. */
  const patch = useCallback(
    (jobId: string, mut: (cur: PublishJobView) => Partial<PublishJobView>) => {
      setJobs((prev) => {
        const cur = prev[jobId];
        if (!cur) return prev;
        return { ...prev, [jobId]: { ...cur, ...mut(cur) } };
      });
    },
    [],
  );

  /** Tear down a watcher cleanly. Called on dismiss + on terminal
   *  states. Idempotent — guards against the stream firing one last
   *  event after we've already given up on the job. */
  const stopWatcher = useCallback((jobId: string) => {
    const w = watchersRef.current.get(jobId);
    if (!w) return;
    w.stopped = true;
    if (w.graceTimer != null) {
      window.clearTimeout(w.graceTimer);
    }
    if (w.pollTimer != null) {
      window.clearTimeout(w.pollTimer);
    }
    watchersRef.current.delete(jobId);
  }, []);

  /** REST polling fallback: query the JobRun row until terminal. Used
   *  when the SSE stream drops — common during deploys, common when
   *  the in-memory ``job_id`` is gone after a tab refresh. */
  const startPolling = useCallback(
    (w: Watcher) => {
      if (w.stopped || w.pollTimer != null) return;
      w.pollUntil = Date.now() + POLL_TIMEOUT_MS;

      const tick = async () => {
        if (w.stopped) return;
        try {
          const r = await api<{
            status: string;
            error_message: string | null;
            result: any;
            paused?: boolean;
            stop_requested?: boolean;
          }>(`/api/job-runs/${w.jobRunId}`);
          // Mirror live control flags into the panel so the buttons can
          // render the correct affordance without a separate fetch.
          patch(w.jobId, () => ({
            paused: !!r.paused,
            stopRequested: !!r.stop_requested,
          }));
          if (r.status === "cancelled") {
            patch(w.jobId, () => ({
              state: "cancelled",
              lastMessage: r.error_message || "Cancelled",
              mhPercent: 100,
              ssPercent: 100,
            }));
            stopWatcher(w.jobId);
            return;
          }
          if (r.status === "done") {
            const cur = jobsRef.current[w.jobId];
            const resolved = resolvePlatformsFromResult(
              r.result,
              w.useSs,
            );
            patch(w.jobId, () => ({
              state: "done",
              lastMessage: "Pipeline complete",
              ...(resolved.mhStatus
                ? { mhStatus: resolved.mhStatus }
                : cur?.mhStatus === "skipped"
                  ? {}
                  : { mhStatus: cur?.mhStatus === "running" ? "done" : cur?.mhStatus ?? "done" }),
              ...(resolved.ssStatus
                ? { ssStatus: resolved.ssStatus }
                : {}),
              ...(resolved.mhUrl ? { mhUrl: resolved.mhUrl } : {}),
              ...(resolved.ssUrl ? { ssUrl: resolved.ssUrl } : {}),
              ...(resolved.mhError ? { mhError: resolved.mhError } : {}),
              ...(resolved.ssError ? { ssError: resolved.ssError } : {}),
              mhPercent: 100,
              ssPercent: 100,
            }));
            stopWatcher(w.jobId);
            return;
          }
          if (r.status === "error") {
            const resolved = resolvePlatformsFromResult(r.result, w.useSs);
            patch(w.jobId, (cur) => ({
              state: "error",
              lastMessage: r.error_message || "Pipeline failed",
              mhStatus:
                resolved.mhStatus ??
                (cur.mhStatus === "running" ? "error" : cur.mhStatus),
              ssStatus:
                resolved.ssStatus ??
                (cur.ssStatus === "running" ? "error" : cur.ssStatus),
              ...(resolved.mhUrl ? { mhUrl: resolved.mhUrl } : {}),
              ...(resolved.ssUrl ? { ssUrl: resolved.ssUrl } : {}),
              ...(resolved.mhError ? { mhError: resolved.mhError } : {}),
              ...(resolved.ssError ? { ssError: resolved.ssError } : {}),
              mhPercent: 100,
              ssPercent: 100,
            }));
            stopWatcher(w.jobId);
            return;
          }
          if (Date.now() > w.pollUntil) {
            patch(w.jobId, (cur) =>
              cur.state === "running"
                ? { state: "error", lastMessage: "lost contact with backend" }
                : {},
            );
            stopWatcher(w.jobId);
            return;
          }
        } catch {
          if (Date.now() > w.pollUntil) {
            patch(w.jobId, (cur) =>
              cur.state === "running"
                ? { state: "error", lastMessage: "lost contact with backend" }
                : {},
            );
            stopWatcher(w.jobId);
            return;
          }
        }
        w.pollTimer = window.setTimeout(tick, POLL_INTERVAL_MS);
      };
      w.pollTimer = window.setTimeout(tick, POLL_INTERVAL_MS);
    },
    [patch, stopWatcher],
  );

  /** Apply one progress event to a job's card. Delivered live over the
   *  single shared WebSocket (see the ``publish_job.event`` subscription
   *  below) instead of a per-job SSE stream — same JSON shape the
   *  backend used to push over ``/api/stream/{jobId}``, only the
   *  transport changed. */
  const applyJobEvent = useCallback(
    (
      w: Watcher,
      evt: { step?: string; status?: string; message?: string; result?: any },
    ) => {
        patch(w.jobId, (cur) => {
          const next: Partial<PublishJobView> = {};
          if (evt.message) next.lastMessage = evt.message;
          // Per-platform error tracking from step prefixes. ``finished``
          // below resolves the canonical state from the result payload.
          const step = evt.step || "";
          const status = evt.status || "";
          const isSsStep = step.startsWith("ss_");
          const isMhStep =
            step.startsWith("mh_") ||
            step === "scrape" ||
            step === "auth" ||
            step === "upload" ||
            step === "create" ||
            step === "pay" ||
            step === "myhome_login" ||
            step === "result";
          if (isSsStep && cur.ssStatus !== "skipped") {
            if (status === "error") {
              next.ssStatus = "error";
              if (evt.message) next.ssError = evt.message;
            }
          } else if (isMhStep) {
            if (status === "error") {
              next.mhStatus = "error";
              if (evt.message) next.mhError = evt.message;
            }
          }

          // Per-job step-sequence progress. Advance the cursor on every
          // event and recompute the percent so each row gets its own
          // bar. ``queue`` events apply to both platforms; otherwise we
          // route by the step's platform prefix. The image-upload step
          // is the longest by far — parse "i/N" from the progress
          // message to interpolate inside the upload bucket so the bar
          // doesn't visually stall while a 16-image upload runs.
          if (step && status !== "error") {
            if (step === "queue" || isMhStep) {
              const newIdx = advanceStepCursor(
                MH_STEP_SEQUENCE,
                cur.mhStepIdx,
                step,
                status,
              );
              if (newIdx !== cur.mhStepIdx) next.mhStepIdx = newIdx;
              let pct = percentFromIdx(newIdx, MH_STEP_SEQUENCE.length);
              if (
                pct != null &&
                step === "upload" &&
                status === "progress" &&
                evt.message
              ) {
                const frac = parseUploadFraction(evt.message);
                if (frac != null) {
                  // Bucket spans from upload-start (idx 5) to upload-done (idx 6).
                  const startPct = percentFromIdx(5, MH_STEP_SEQUENCE.length) ?? 0;
                  const endPct = percentFromIdx(6, MH_STEP_SEQUENCE.length) ?? 0;
                  pct = Math.round(startPct + (endPct - startPct) * frac);
                }
              }
              if (pct != null && (cur.mhPercent == null || pct > cur.mhPercent)) {
                next.mhPercent = pct;
              }
            }
            if (step === "queue" || (isSsStep && cur.ssStatus !== "skipped")) {
              const newIdx = advanceStepCursor(
                SS_STEP_SEQUENCE,
                cur.ssStepIdx,
                step,
                status,
              );
              if (newIdx !== cur.ssStepIdx) next.ssStepIdx = newIdx;
              let pct = percentFromIdx(newIdx, SS_STEP_SEQUENCE.length);
              if (
                pct != null &&
                step === "ss_upload" &&
                status === "progress" &&
                evt.message
              ) {
                const frac = parseUploadFraction(evt.message);
                if (frac != null) {
                  // Bucket spans from ss_upload-start (idx 5) to ss_upload-done (idx 6).
                  const startPct = percentFromIdx(5, SS_STEP_SEQUENCE.length) ?? 0;
                  const endPct = percentFromIdx(6, SS_STEP_SEQUENCE.length) ?? 0;
                  pct = Math.round(startPct + (endPct - startPct) * frac);
                }
              }
              if (pct != null && (cur.ssPercent == null || pct > cur.ssPercent)) {
                next.ssPercent = pct;
              }
            }
          }

          if (step === "finished") {
            const resolved = resolvePlatformsFromResult(
              evt.result,
              cur.useSs,
            );
            if (evt.status === "done") {
              next.state = "done";
            } else if (evt.status === "cancelled") {
              next.state = "cancelled";
              next.mhPercent = 100;
              next.ssPercent = 100;
            } else if (evt.status === "error") {
              next.state = "error";
            }
            if (resolved.mhStatus) next.mhStatus = resolved.mhStatus;
            if (resolved.ssStatus) next.ssStatus = resolved.ssStatus;
            if (resolved.mhUrl) next.mhUrl = resolved.mhUrl;
            if (resolved.ssUrl) next.ssUrl = resolved.ssUrl;
            if (resolved.mhError) next.mhError = resolved.mhError;
            if (resolved.ssError) next.ssError = resolved.ssError;
            // Snap finished/skipped platforms to 100% so the bar
            // doesn't stop short when an optional step (e.g. ``pay``)
            // didn't fire.
            const finalMh = next.mhStatus ?? cur.mhStatus;
            const finalSs = next.ssStatus ?? cur.ssStatus;
            if (finalMh === "done" || finalMh === "error") next.mhPercent = 100;
            if (finalSs === "done" || finalSs === "error" || finalSs === "skipped") {
              next.ssPercent = 100;
            }
          }
          return next;
        });
      if (evt.step === "finished") {
        stopWatcher(w.jobId);
      }
    },
    [patch, stopWatcher],
  );

  // Live delivery: every job's progress rides this one subscription on
  // the shared WebSocket, keyed by ``job_id`` back to the right watcher.
  // Replaces the old per-job ``EventSource`` — a bulk publish of N jobs
  // used to open N of those (plus a 4s REST poll per job once any
  // dropped), which is what made the browser choke on large batches.
  useWSEvent("publish_job.event", (ev) => {
    const w = watchersRef.current.get(ev.job_id);
    if (!w || w.stopped) return;
    w.gotWsEvent = true;
    if (w.graceTimer != null) {
      window.clearTimeout(w.graceTimer);
      w.graceTimer = null;
    }
    applyJobEvent(w, ev);
  });

  const { connected: wsConnected } = useWS();
  const wsConnectedRef = useRef(wsConnected);
  wsConnectedRef.current = wsConnected;

  /** Arm the WS-vs-poll fallback for a freshly attached watcher. If the
   *  socket isn't even connected yet, skip straight to polling; otherwise
   *  give the live push ``GRACE_MS`` to prove it's flowing before falling
   *  back — covers the socket dropping mid-job or the backend's
   *  in-memory job having vanished (deploy, restart). */
  const scheduleGrace = useCallback(
    (w: Watcher) => {
      if (w.stopped) return;
      if (!wsConnectedRef.current) {
        startPolling(w);
        return;
      }
      w.graceTimer = window.setTimeout(() => {
        w.graceTimer = null;
        if (w.stopped || w.gotWsEvent) return;
        startPolling(w);
      }, GRACE_MS);
    },
    [startPolling],
  );

  // If the socket drops while jobs are in flight, don't wait out each
  // watcher's grace window — switch them to polling immediately since we
  // already know the live channel is down.
  const prevWsConnectedRef = useRef(wsConnected);
  useEffect(() => {
    if (prevWsConnectedRef.current && !wsConnected) {
      for (const w of watchersRef.current.values()) {
        if (w.stopped || w.pollTimer != null) continue;
        if (w.graceTimer != null) {
          window.clearTimeout(w.graceTimer);
          w.graceTimer = null;
        }
        startPolling(w);
      }
    }
    prevWsConnectedRef.current = wsConnected;
  }, [wsConnected, startPolling]);

  const register = useCallback(
    (j: PublishJobRegisterIn) => {
      // A per-platform retry leaves the other portal untouched — render it
      // as ``skipped`` (100%) from the start so the row doesn't show a
      // chip that spins forever waiting for events that never come.
      const mhSkipped = j.onlyPlatform === "ss";
      const ssSkipped = j.onlyPlatform === "myhome" || !j.useSs;
      const initial: PublishJobView = {
        jobId: j.jobId,
        jobRunId: j.jobRunId,
        origin: j.origin,
        title: j.title,
        sourceUrl: j.sourceUrl,
        agentLabel: j.agentLabel,
        agentAvatarUrl: j.agentAvatarUrl ?? null,
        triggeredByName: j.triggeredByName ?? null,
        triggeredByAvatarUrl: j.triggeredByAvatarUrl ?? null,
        useSs: j.useSs,
        isForeign: !!j.isForeign,
        onlyPlatform: j.onlyPlatform,
        state: "running",
        lastMessage: "starting…",
        startedAt: j.startedAt ?? Date.now(),
        mhStatus: mhSkipped ? "skipped" : "running",
        ssStatus: ssSkipped ? "skipped" : "running",
        mhUrl: null,
        ssUrl: null,
        mhError: null,
        ssError: null,
        mhPercent: mhSkipped ? 100 : null,
        ssPercent: ssSkipped ? 100 : null,
        mhStepIdx: -1,
        ssStepIdx: -1,
      };
      setJobs((prev) => ({ ...prev, [j.jobId]: initial }));
      attachWatcher(j.jobId, j.jobRunId, j.useSs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Create the watcher for a card. Idempotent — calling twice for the
   *  same jobId is a no-op (the second watcher would race the first onto
   *  ``setJobs``). Used by ``register`` for fresh cards and by the
   *  hydration effect for cards rebuilt from localStorage that need
   *  their progress watcher rewired after a page refresh. */
  function attachWatcher(jobId: string, jobRunId: string, useSs: boolean) {
    if (watchersRef.current.has(jobId)) return;
    const w: Watcher = {
      jobId,
      jobRunId,
      useSs,
      graceTimer: null,
      gotWsEvent: false,
      pollTimer: null,
      pollUntil: 0,
      stopped: false,
    };
    watchersRef.current.set(jobId, w);
    scheduleGrace(w);
  }

  // Re-attach watchers to every hydrated ``running`` card on mount.
  // localStorage rehydration restores ``jobs`` but does not call
  // ``register``, so cards mid-flight at refresh-time have no watcher
  // and freeze at whatever progress they were last painted with
  // ("MyHome 73% forever" after the user reloads). Run-once: subsequent
  // ``register`` calls (from the agency poller / publish flow) install
  // their own watchers as before. Cleanup tears down every outstanding
  // watcher on true unmount (app teardown / dev hot-reload) — the
  // provider otherwise persists for the app's whole lifetime.
  useEffect(() => {
    for (const view of Object.values(jobsRef.current)) {
      if (view.state !== "running") continue;
      attachWatcher(view.jobId, view.jobRunId, view.useSs);
    }
    const watchers = watchersRef.current;
    return () => {
      for (const w of watchers.values()) {
        w.stopped = true;
        if (w.graceTimer != null) window.clearTimeout(w.graceTimer);
        if (w.pollTimer != null) window.clearTimeout(w.pollTimer);
      }
      watchers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(
    (jobId: string) => {
      stopWatcher(jobId);
      // Tombstone is keyed by the *JobRun ID* — the only stable
      // identifier across (browser refresh, backend restart, foreign
      // poll cycles). Using jobId here let the agency poller resurrect
      // the same JobRun under a different jobId after a backend bounce.
      const view = jobsRef.current[jobId];
      if (view) dismissedRef.current.set(view.jobRunId, Date.now());
      persistDismissed();
      setJobs((prev) => {
        if (!prev[jobId]) return prev;
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    },
    [stopWatcher, persistDismissed],
  );

  const stopBatchWatcher = useCallback((batchId: string) => {
    const w = batchWatchersRef.current.get(batchId);
    if (!w) return;
    w.stopped = true;
    if (w.pollTimer != null) window.clearTimeout(w.pollTimer);
    batchWatchersRef.current.delete(batchId);
  }, []);

  const clearFinished = useCallback(() => {
    const now = Date.now();
    setJobs((prev) => {
      const next: Record<string, PublishJobView> = {};
      for (const [id, j] of Object.entries(prev)) {
        if (j.state === "running") next[id] = j;
        // Tombstone every cleared job so the poller doesn't re-add it.
        // Keyed by JobRun ID — see the ``dismiss`` comment above.
        else dismissedRef.current.set(j.jobRunId, now);
      }
      return next;
    });
    persistDismissed();

    // Batches are a separate state slice (post d928efa aggregation) — a
    // settled bulk-publish batch shows as "finished" via its done/failed
    // counts (see Panel's ``statsWithBatches``) but lives here, not in
    // ``jobs``, so it needs its own sweep mirroring ``dismissBatch``.
    for (const b of Object.values(batchesRef.current)) {
      if (b.running > 0) continue;
      stopBatchWatcher(b.batchId);
      dismissedBatchesRef.current.set(b.batchId, now);
    }
    persistDismissedBatches();
    setBatches((prev) => {
      const next: Record<string, PublishBatchView> = {};
      for (const [id, b] of Object.entries(prev)) {
        if (b.running > 0) next[id] = b;
      }
      return next;
    });
  }, [persistDismissed, persistDismissedBatches, stopBatchWatcher]);

  /** Mutate one batch's aggregate record. */
  const patchBatch = useCallback(
    (batchId: string, mut: (cur: PublishBatchView) => Partial<PublishBatchView>) => {
      setBatches((prev) => {
        const cur = prev[batchId];
        if (!cur) return prev;
        return { ...prev, [batchId]: { ...cur, ...mut(cur) } };
      });
    },
    [],
  );

  /** One aggregate-summary poll tick. Plain interval, no WS involvement —
   *  a single ``GET`` answers "how's this batch doing" regardless of
   *  whether it holds 5 listings or 5,000, so this can never turn into
   *  the per-job request storm the old per-job watchers produced. */
  const startBatchPolling = useCallback(
    (w: BatchWatcher) => {
      if (w.stopped || w.pollTimer != null) return;
      w.pollUntil = Date.now() + BATCH_POLL_TIMEOUT_MS;

      const tick = async () => {
        if (w.stopped) return;
        try {
          const r = await api<BatchSummary>(
            `/api/job-runs/batch/${w.batchId}`,
          );
          const running = r.pending + r.running;
          const failed = r.error + r.cancelled;
          patchBatch(w.batchId, () => ({
            total: r.total,
            running,
            done: r.done,
            failed,
            paused: r.paused,
            resolved: running === 0,
          }));
          if (running === 0) {
            stopBatchWatcher(w.batchId);
            return;
          }
        } catch {
          if (Date.now() > w.pollUntil) {
            stopBatchWatcher(w.batchId);
            return;
          }
        }
        if (Date.now() > w.pollUntil) {
          stopBatchWatcher(w.batchId);
          return;
        }
        w.pollTimer = window.setTimeout(tick, BATCH_POLL_INTERVAL_MS);
      };
      // First read fires immediately so the panel gets real counts within
      // one round-trip instead of waiting out the full interval.
      tick();
    },
    [patchBatch, stopBatchWatcher],
  );

  /** One-shot authoritative fetch of a batch's aggregate — no interval. Used
   *  for batches discovered via the connect snapshot: an already-finished
   *  batch gets no transition push, so this resolves it; a live batch is then
   *  driven by ``bulk_batch.progress`` pushes. */
  const fetchBatchOnce = useCallback(
    async (batchId: string) => {
      try {
        const r = await api<BatchSummary>(`/api/job-runs/batch/${batchId}`);
        const running = r.pending + r.running;
        const failed = r.error + r.cancelled;
        patchBatch(batchId, () => ({
          total: r.total,
          running,
          done: r.done,
          failed,
          paused: r.paused,
          resolved: running === 0,
        }));
        if (running === 0) stopBatchWatcher(batchId);
      } catch {
        /* a push or the disconnected-fallback poll reconciles either way */
      }
    },
    [patchBatch, stopBatchWatcher],
  );

  const registerBatch = useCallback(
    (b: PublishBatchRegisterIn) => {
      if (batchWatchersRef.current.has(b.batchId)) return;
      if (dismissedBatchesRef.current.has(b.batchId)) return;
      const initial: PublishBatchView = {
        batchId: b.batchId,
        useSs: b.useSs,
        createdAt: Date.now(),
        total: b.total,
        running: b.total,
        done: 0,
        failed: 0,
        paused: 0,
        resolved: false,
      };
      setBatches((prev) => ({ ...prev, [b.batchId]: initial }));
      const w: BatchWatcher = {
        batchId: b.batchId,
        pollTimer: null,
        pollUntil: 0,
        stopped: false,
      };
      batchWatchersRef.current.set(b.batchId, w);
      // While the socket is up, aggregate progress arrives via the
      // ``bulk_batch.progress`` push — no interval poll. Only fall back to
      // polling when disconnected (the connection-change effect below also
      // starts/stops polling on transitions).
      if (!wsConnectedRef.current) startBatchPolling(w);
    },
    [startBatchPolling],
  );

  const pauseBatch = useCallback((batchId: string) => {
    api(`/api/job-runs/batch/${batchId}/pause`, { method: "POST" }).catch(
      () => {
        /* the next poll tick reconciles either way */
      },
    );
  }, []);

  const resumeBatch = useCallback((batchId: string) => {
    api(`/api/job-runs/batch/${batchId}/resume`, { method: "POST" }).catch(
      () => {
        /* the next poll tick reconciles either way */
      },
    );
  }, []);

  const stopBatch = useCallback((batchId: string) => {
    api(`/api/job-runs/batch/${batchId}/stop`, { method: "POST" }).catch(
      () => {
        /* the next poll tick reconciles either way */
      },
    );
  }, []);

  const dismissBatch = useCallback(
    (batchId: string) => {
      stopBatchWatcher(batchId);
      dismissedBatchesRef.current.set(batchId, Date.now());
      persistDismissedBatches();
      setBatches((prev) => {
        if (!prev[batchId]) return prev;
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    },
    [stopBatchWatcher, persistDismissedBatches],
  );

  // Tear down every outstanding batch watcher on unmount — mirrors the
  // per-job watcher cleanup above.
  useEffect(() => {
    const watchers = batchWatchersRef.current;
    return () => {
      for (const w of watchers.values()) {
        w.stopped = true;
        if (w.pollTimer != null) window.clearTimeout(w.pollTimer);
      }
      watchers.clear();
    };
  }, []);

  // Live aggregate batch progress over the shared socket — the batch-level
  // analogue of ``publish_job.event``. Replaces the per-batch 4s poll of
  // ``/api/job-runs/batch/{id}``: the server pushes counts on spawn and on
  // each (debounced) transition to every watcher. First sighting registers
  // the batch (discovery for teammates), so no discovery poll is needed
  // while connected either.
  useWSEvent("bulk_batch.progress", (ev) => {
    if (dismissedBatchesRef.current.has(ev.batch_id)) return;
    // Idempotent: registers on first sighting, no-ops once tracked. Seeds
    // running=total; the patch immediately below corrects to real counts.
    registerBatch({
      batchId: ev.batch_id,
      total: ev.total,
      useSs: ev.use_ss,
    });
    const running = ev.pending + ev.running;
    const failed = ev.error + ev.cancelled;
    patchBatch(ev.batch_id, () => ({
      total: ev.total,
      running,
      done: ev.done,
      failed,
      paused: ev.paused,
      resolved: running === 0,
    }));
    if (running === 0) stopBatchWatcher(ev.batch_id);
  });

  // Mirror the per-job connection-change handler for batches: when the
  // socket drops, start the aggregate-summary poll for every live batch as a
  // fallback; when it (re)connects, stop those polls — pushes take over and
  // the discovery effect below re-snapshots.
  useEffect(() => {
    if (wsConnected) {
      for (const w of batchWatchersRef.current.values()) {
        if (w.pollTimer != null) {
          window.clearTimeout(w.pollTimer);
          w.pollTimer = null;
        }
      }
    } else {
      for (const w of batchWatchersRef.current.values()) {
        if (!w.stopped && w.pollTimer == null) startBatchPolling(w);
      }
    }
  }, [wsConnected, startBatchPolling]);

  // Sticky across effect re-runs (the effect now re-runs on every connect/
  // disconnect): a 403 means the viewer lacks bulk-publish access, so we
  // never hit the endpoint again this session.
  const discoveryDisabledRef = useRef(false);

  // Agency-wide bulk-publish discovery. One snapshot of the agency's recent
  // JobRun rows on mount and on every WS (re)connect, to hydrate in-flight
  // batches / foreign jobs the client may have missed. While the socket is
  // UP we do NOT interval-poll — live updates arrive via ``bulk_batch.progress``
  // and ``publish_job.event`` pushes. Only when the socket is DOWN do we fall
  // back to the adaptive poll so discovery still works during an outage. The
  // backend gates this endpoint by ``/market/publish`` page access — viewers
  // without bulk-publish permission get a 403 and we stop (their panel stays
  // limited to their own jobs).
  useEffect(() => {
    // This is a *discovery* poll only: it finds teammates' bulk runs and
    // registers them into the panel. Once registered, live progress arrives
    // via SSE / the per-job & per-batch watchers — NOT from this endpoint —
    // and the viewer's own jobs are registered directly by the publish flow.
    // So it never needs a tight cadence: poll slowly while idle (the common
    // case — no bulk running anywhere) and only speed up once a run is
    // actually in flight. Worst case a teammate's brand-new bulk shows up
    // one IDLE interval late, which is fine for a background awareness panel.
    const IDLE_POLL_MS = 30000;
    const ACTIVE_POLL_MS = 8000;
    let cancelled = false;
    let timer: number | null = null;

    const poll = async () => {
      if (cancelled || discoveryDisabledRef.current) return;
      let nextDelay = IDLE_POLL_MS;
      try {
        const r = await api<{ jobs: AgencyBulkJob[] }>(
          "/api/market/bulk-jobs",
        );
        // Any live (pending/running) run anywhere in the agency → poll fast
        // so sibling jobs surface quickly; otherwise back off to idle.
        if (
          (r.jobs ?? []).some(
            (j) => j.status === "pending" || j.status === "running",
          )
        ) {
          nextDelay = ACTIVE_POLL_MS;
        }
        if (cancelled) return;
        // How far back to surface already-finished foreign jobs in the
        // panel. Anything terminal older than this stays out of the
        // panel (we don't want every page load to backfill 24h of
        // history), but recent failures still show so a director can
        // see *why* a teammate's publish died without hunting in logs
        // or Telegram. Done/cancelled jobs are included in the same
        // window for completeness.
        const RECENT_TERMINAL_MS = 30 * 60 * 1000;
        // Build a JobRun-ID → existing-card-key lookup once per poll
        // so the de-dupe below is O(1). The map's key is the JobRun's
        // UUID — the only stable identifier across (a) the spawner's
        // browser (registered with in-memory ``job_id``), (b) a teammate
        // watching via the poller, and (c) the *same* browser after a
        // backend restart (where the in-memory registry is gone and the
        // poller now hands back ``job_id=null``). Keying de-dupe on
        // jobId let the same JobRun map to two different keys across
        // those scenarios, surfacing as a phantom duplicate card.
        const cardsByJobRunId = new Map<string, string>();
        for (const [cardKey, view] of Object.entries(jobsRef.current)) {
          cardsByJobRunId.set(view.jobRunId, cardKey);
        }

        // Group every batch row so we register ONE aggregate per batch
        // instead of one watcher per job — this is what used to make a
        // teammate's several-hundred-listing bulk publish spin up
        // several hundred individual watchers in *every other* agency
        // member's browser just from having this poller running.
        const batchGroups = new Map<string, { count: number; useSs: boolean }>();
        for (const j of r.jobs ?? []) {
          if (!j.batch_id) continue;
          if (batchesRef.current[j.batch_id]) continue;
          if (dismissedBatchesRef.current.has(j.batch_id)) continue;
          const g = batchGroups.get(j.batch_id) ?? { count: 0, useSs: false };
          g.count += 1;
          if (j.use_ss) g.useSs = true;
          batchGroups.set(j.batch_id, g);
        }
        for (const [batchId, g] of batchGroups) {
          // ``total`` here is only a first estimate (rows within this
          // endpoint's 24h window). Register, then reconcile to the
          // authoritative aggregate: when connected, one confirming fetch
          // (a live batch is then driven by pushes; a finished one resolves);
          // when disconnected, ``registerBatch`` already started the fallback
          // poll which overwrites the estimate on its first tick.
          registerBatch({ batchId, total: g.count, useSs: g.useSs });
          if (wsConnectedRef.current) void fetchBatchOnce(batchId);
        }

        for (const j of r.jobs ?? []) {
          // Batch rows are aggregated above — no per-job watcher.
          if (j.batch_id) continue;
          // Use the in-memory ``job_id`` when present so SSE works; if
          // the worker thread has exited, fall back to a synthetic key
          // derived from ``job_run_id`` so the entry still appears in
          // the panel and the polling watcher resolves status from the
          // JobRun row.
          const jobId = j.job_id ?? `run:${j.job_run_id}`;
          // De-dupe by JobRun ID, not by the (unstable) constructed
          // jobId. If a card already exists for this JobRun under ANY
          // key, skip — that's the same publish attempt we're already
          // tracking.
          if (cardsByJobRunId.has(j.job_run_id)) continue;
          // Dismissal tombstones are written keyed by the JobRun ID
          // (see ``dismiss`` below) so user-cleared rows can't be
          // resurrected by the poller even after a key change.
          if (dismissedRef.current.has(j.job_run_id)) continue;
          // Always pick up pending/running jobs; for terminal jobs
          // (error / done / cancelled), only the ones finished within
          // the last 30 min so the panel surfaces recent teammate
          // failures alongside live ones. The watcher's REST poll
          // below resolves terminal status + error_message immediately.
          const isLive = j.status === "pending" || j.status === "running";
          if (!isLive) {
            const finishedMs = j.finished_at
              ? Date.parse(j.finished_at)
              : Number.NaN;
            if (
              Number.isNaN(finishedMs) ||
              Date.now() - finishedMs > RECENT_TERMINAL_MS
            ) {
              continue;
            }
          }
          // Mark as foreign before register so the persistence effect
          // (which fires synchronously after setJobs) skips it. Own
          // jobs (kicked off via the publish flow) call ``register``
          // directly and never land in this set.
          if (!j.is_mine) foreignJobIdsRef.current.add(jobId);
          const startedMs = j.started_at
            ? Date.parse(j.started_at)
            : Number.NaN;
          register({
            jobId,
            jobRunId: j.job_run_id,
            origin: "bulk",
            title: j.source_url,
            sourceUrl: j.source_url,
            agentLabel: j.agent_name,
            agentAvatarUrl: j.agent_avatar_url,
            triggeredByName: j.triggered_by_name,
            triggeredByAvatarUrl: j.triggered_by_avatar_url,
            useSs: !!j.use_ss,
            startedAt: Number.isNaN(startedMs) ? undefined : startedMs,
            isForeign: true,
          });
        }
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 403) {
          discoveryDisabledRef.current = true; // viewer lacks bulk-publish
        }
        // network blips, 5xx, etc — silently retry on the next (idle) tick.
      }
      // Keep an interval ONLY while the socket is down (fallback discovery).
      // While connected this ran once as a snapshot; live updates ride the WS,
      // and the effect re-runs (re-snapshots) on the next reconnect.
      if (
        !cancelled &&
        !discoveryDisabledRef.current &&
        !wsConnectedRef.current
      ) {
        timer = window.setTimeout(poll, nextDelay);
      }
    };
    // Poll right away so a fresh page load surfaces agency-wide jobs
    // within an API round-trip rather than after the 5s cadence —
    // matters most after a full browser refresh, where localStorage
    // no longer carries foreign rows.
    timer = window.setTimeout(poll, 50);
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
    // Re-run on connect/disconnect so we re-snapshot on reconnect and switch
    // the fallback poll on/off. ``register``/``registerBatch`` are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected]);

  const value = useMemo<PublishProgressContextValue>(
    () => ({
      jobs,
      register,
      dismiss,
      clearFinished,
      batches,
      registerBatch,
      pauseBatch,
      resumeBatch,
      stopBatch,
      dismissBatch,
    }),
    [
      jobs,
      register,
      dismiss,
      clearFinished,
      batches,
      registerBatch,
      pauseBatch,
      resumeBatch,
      stopBatch,
      dismissBatch,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePublishProgress(): PublishProgressContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      "usePublishProgress must be used inside <PublishProgressProvider>",
    );
  }
  return v;
}
